import React from 'react';
import { Link } from 'react-router-dom';
import { LogOut, Settings } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@shared/ui/dropdown-menu';
import { Avatar, AvatarFallback } from '@shared/ui/avatar';
import { Button } from '@shared/ui/button';
import { useAuth } from '@shared/auth/AuthContext';

export const UserMenu: React.FC = () => {
  const { user, logout, hasPermission } = useAuth();

  if (!user) {
    return (
      <Button size="sm" asChild variant="secondary" className="h-8 text-xs">
        <Link to="/login">Sign In</Link>
      </Button>
    );
  }

  const initials = user.email
    ? user.email.substring(0, 2).toUpperCase()
    : 'U';

  const canManageSettings = hasPermission('platform.tenant.manage');

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="rounded-full ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          aria-label="User profile menu"
        >
          <Avatar className="h-7 w-7 text-xs bg-primary text-primary-foreground font-semibold cursor-pointer">
            <AvatarFallback className="bg-primary/20 text-primary font-bold">
              {initials}
            </AvatarFallback>
          </Avatar>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56" sideOffset={8}>
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-1">
            <p className="text-xs font-semibold leading-none text-foreground truncate">
              {user.email}
            </p>
            {user.role && (
              <p className="text-[10px] text-muted-foreground uppercase font-mono tracking-wider">
                {user.role}
              </p>
            )}
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {canManageSettings && (
          <DropdownMenuItem asChild>
            <Link to="/settings" className="flex items-center gap-2 cursor-pointer">
              <Settings className="h-3.5 w-3.5 text-muted-foreground" />
              <span>Settings</span>
            </Link>
          </DropdownMenuItem>
        )}
        <DropdownMenuItem
          onClick={() => logout()}
          className="flex items-center gap-2 text-destructive focus:text-destructive cursor-pointer"
        >
          <LogOut className="h-3.5 w-3.5" />
          <span>Sign Out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
